import React from 'react';
import {
  SharpEdge,
  Thickness,
  HoleDiameter,
  Embossed,
  Engraved,
  SurfPlane,
  SurfCylinder,
  SurfCone,
  SurfTorus,
  SurfSphere,
  SurfNurbs,
  ThreeDP,
} from 'icons';

const toRad = Math.PI / 180.0;
const toDeg = 180.0 / Math.PI;

export const getUnitConversionFactor = unit => {
  if (unit) {
    if (typeof unit === 'string' || unit instanceof String) {
      switch (unit) {
        case 'mm':
        case 'MM':
          return 1.0;
        case 'cm':
        case 'CM':
          return 0.1;
        case 'inch':
        case 'INCH':
        case 'Inch':
          return 0.0393701;
        default:
          break;
      }
    }
  }
  return 1.0;
};

export const getUnitsLabel = unitFactor => {
  if (unitFactor) {
    if (
      !Number.isNaN(parseFloat(unitFactor)) &&
      !Number.isNaN(unitFactor - 0)
    ) {
      const epsilon = 0.001;
      if (Math.abs(1 - unitFactor) < epsilon) return 'mm';
      if (Math.abs(0.1 - unitFactor) < epsilon) return 'cm';
      if (Math.abs(0.0393701 - unitFactor) < epsilon) return 'inch';
    }
  }
  return null;
};

// cutoffs for available fabrication processes
const DfmProcessCutoffsList = [
  {
    key: 'FDM',
    index: 0,
    text: 'Fused Deposition Modeling (FDM)',
    value: {
      thickness: 0.8,
      sharpness: 120 * toRad,
      holeRadius: 1.0,
      embossed: 2.0,
      engraved: 2.0,
    },
  },
  {
    key: 'SLA',
    index: 1,
    text: 'Stereolithography (SLA)',
    value: {
      thickness: 0.5,
      sharpness: 120 * toRad,
      holeRadius: 0.25,
      embossed: 0.4,
      engraved: 0.4,
    },
  },
  {
    key: 'SLS',
    index: 2,
    text: 'Selective Laser Sintering (SLS)',
    value: {
      thickness: 0.75,
      sharpness: 120 * toRad,
      holeRadius: 0.75,
      embossed: 1.0,
      engraved: 1.0,
    },
  },
  {
    key: 'MJF',
    index: 3,
    text: 'Multi Jet Fusion (MJF)',
    value: {
      thickness: 0.5,
      sharpness: 120 * toRad,
      holeRadius: 0.5,
      embossed: 0.5,
      engraved: 0.5,
    },
  },
  {
    key: 'PolyJet',
    index: 4,
    text: 'PolyJet',
    value: {
      thickness: 0.76,
      sharpness: 120 * toRad,
      holeRadius: 0.76,
      embossed: 0.76,
      engraved: 0.76,
    },
  },
  {
    key: 'DMLS',
    index: 5,
    text: 'Digital Light Synthesis (DLS)',
    value: {
      thickness: 1.0,
      sharpness: 120 * toRad,
      holeRadius: 1.0,
      embossed: 0.5,
      engraved: 0.6,
    },
  },
  {
    key: 'TEST1',
    index: 6,
    text: 'Test 1.0',
    value: {
      thickness: 1.0,
      sharpness: 120 * toRad,
      holeRadius: 1.0,
      embossed: 1.0,
      engraved: 1.0,
    },
  },
  {
    key: 'TEST15',
    index: 7,
    text: 'Test 1.5',
    value: {
      thickness: 1.5,
      sharpness: 120 * toRad,
      holeRadius: 1.5,
      embossed: 1.5,
      engraved: 1.5,
    },
  },
  {
    key: 'TEST2',
    index: 8,
    text: 'Test 2.0',
    value: {
      thickness: 2.0,
      sharpness: 120 * toRad,
      holeRadius: 2.0,
      embossed: 2.0,
      engraved: 2.0,
    },
  },
  {
    key: 'TEST3',
    index: 9,
    text: 'Test 3.0',
    value: {
      thickness: 3.0,
      sharpness: 135 * toRad,
      holeRadius: 3.0,
      embossed: 3.0,
      engraved: 3.0,
    },
  },
];

// fabrication process cutoffs (matches [MeshAnalysisParameters] on server)
export class DfmProcessCutoffs {
  processId = null;

  // cutoffs for each Dfm feature
  units = 'mm';
  unitScale = 1;
  thickness = null;
  sharpness = null;
  holeRadius = null;
  embossed = null;
  engraved = null;

  constructor(processId, units = 'mm') {
    this.processId = processId;
    this.units = units;
    this.unitScale = getUnitConversionFactor(units);

    // get cutoffs from DfmProcessCutoffsList
    const cutoffs = DfmProcessCutoffsList.find(obj => {
      return obj.key === processId;
    });
    if (cutoffs) {
      this.thickness = cutoffs.value.thickness * this.unitScale;
      this.sharpness = cutoffs.value.sharpness; // deg, so no units apply
      this.holeRadius = cutoffs.value.holeRadius * this.unitScale;
      this.embossed = cutoffs.value.embossed * this.unitScale;
      this.engraved = cutoffs.value.engraved * this.unitScale;
    }
  }

  get isEmpty() {
    return (
      this.thickness === null &&
      this.sharpness === null &&
      this.holeRadius === null &&
      this.embossed === null &&
      this.engraved === null
    );
  }

  getFeatureCutoff(featureType, dfmEnum) {
    if (dfmEnum && !this.isEmpty) {
      switch (featureType) {
        case dfmEnum.DfmAnalysisSharpEdges:
        case dfmEnum.DfmAnalysisSharpEdges.value:
          return Math.round(((this.sharpness * 180) / Math.PI) * 10) / 10;
        case dfmEnum.DfmAnalysisThickness:
        case dfmEnum.DfmAnalysisThickness.value:
          return this.thickness;
        case dfmEnum.DfmAnalysisHoleRadius:
        case dfmEnum.DfmAnalysisHoleRadius.value:
          return this.holeRadius;
        case dfmEnum.DfmAnalysisEmbossed:
        case dfmEnum.DfmAnalysisEmbossed.value:
          return this.embossed;
        case dfmEnum.DfmAnalysisEngraved:
        case dfmEnum.DfmAnalysisEngraved.value:
          return this.engraved;
        default:
          break;
      }
    }
    return null;
  }
}

// Dfm Feature Icon utility
export const getDfmFeatureIcon = (featureType, dfmEnum) => {
  if (dfmEnum) {
    switch (featureType) {
      case dfmEnum.DfmAnalysisSharpEdges:
      case dfmEnum.DfmAnalysisSharpEdges.value:
        return <SharpEdge />;
      case dfmEnum.DfmAnalysisThickness:
      case dfmEnum.DfmAnalysisThickness.value:
        return <Thickness />;
      case dfmEnum.DfmAnalysisHoleRadius:
      case dfmEnum.DfmAnalysisHoleRadius.value:
        return <HoleDiameter />;
      case dfmEnum.DfmAnalysisEmbossed:
      case dfmEnum.DfmAnalysisEmbossed.value:
        return <Embossed />;
      case dfmEnum.DfmAnalysisEngraved:
      case dfmEnum.DfmAnalysisEngraved.value:
        return <Engraved />;
      default:
        break;
    }
  }
  return null;
};

/*
  Topo-FEATURE
*/

export const getFeatureIcon = (featureType, featEnum) => {
  if (featEnum) {
    switch (featureType) {
      case -1:
        return <Thickness />;
      case featEnum.TopoFeatureHole:
      case featEnum.TopoFeatureHole.value:
        return <HoleDiameter />;
      case featEnum.TopoFeatureSlot:
      case featEnum.TopoFeatureSlot.value:
        return <HoleDiameter />;
      case featEnum.TopoFeatureBoss:
      case featEnum.TopoFeatureBoss.value:
        return <Embossed />;
      case featEnum.TopoFeatureGusset:
      case featEnum.TopoFeatureGusset.value:
        return <Embossed />;
      case featEnum.TopoFeatureNotch:
      case featEnum.TopoFeatureNotch.value:
        return <HoleDiameter />;
      case featEnum.TopoFeatureCavity:
      case featEnum.TopoFeatureCavity.value:
        return <HoleDiameter />;
      case featEnum.TopoFeaturePocket:
      case featEnum.TopoFeaturePocket.value:
        return <HoleDiameter />;
      default:
        break;
    }
  }
  return null;
};

// round

const roundTo = (num, decimals) => {
  if (decimals === 1) return Math.round(num * 10) / 10;
  if (decimals === 2) return Math.round(num * 100) / 100;
  if (decimals === 3) return Math.round(num * 1000) / 1000;

  const fact = Math.pow(10, decimals);
  return Math.round(num * fact) / fact;
};

/*
  SURFACES
*/

export const getSurfaceIcon = (surfType, surfEnum) => {
  if (surfEnum) {
    switch (surfType) {
      case surfEnum.SurfaceTypeNull:
      case surfEnum.SurfaceTypeNull.value:
        return <ThreeDP />;
      case surfEnum.SurfaceTypePlane:
      case surfEnum.SurfaceTypePlane.value:
      case surfEnum.SurfaceTypePseudoPlane:
      case surfEnum.SurfaceTypePseudoPlane.value:
        return <SurfPlane />;
      case surfEnum.SurfaceTypeCylinder:
      case surfEnum.SurfaceTypeCylinder.value:
        return (
          <SurfCylinder
            style={{
              width: 20,
              height: 20,
              marginLeft: 2,
            }}
          />
        );
      case surfEnum.SurfaceTypeCone:
      case surfEnum.SurfaceTypeCone.value:
        return <SurfCone />;
      case surfEnum.SurfaceTypeTorus:
      case surfEnum.SurfaceTypeTorus.value:
        return <SurfTorus />;
      case surfEnum.SurfaceTypeSphere:
      case surfEnum.SurfaceTypeSphere.value:
        return <SurfSphere />;
      case surfEnum.SurfaceTypeNurbs:
      case surfEnum.SurfaceTypeNurbs.value:
        return <SurfNurbs />;
      // TODO: others unused by now
      default:
        break;
    }
  }
  return null;
};

export const getSurfaceLabel = (surfType, surfEnum) => {
  if (surfEnum) {
    switch (surfType) {
      case surfEnum.SurfaceTypeNull:
      case surfEnum.SurfaceTypeNull.value:
        return 'Unknown';
      case surfEnum.SurfaceTypePlane:
      case surfEnum.SurfaceTypePlane.value:
        return 'Plane';
      case surfEnum.SurfaceTypePseudoPlane:
      case surfEnum.SurfaceTypePseudoPlane.value:
        return 'Pseudo-Plane';
      case surfEnum.SurfaceTypeCylinder:
      case surfEnum.SurfaceTypeCylinder.value:
        return 'Cylinder';
      case surfEnum.SurfaceTypeCone:
      case surfEnum.SurfaceTypeCone.value:
        return 'Cone';
      case surfEnum.SurfaceTypeTorus:
      case surfEnum.SurfaceTypeTorus.value:
        return 'Torus';
      case surfEnum.SurfaceTypeSphere:
      case surfEnum.SurfaceTypeSphere.value:
        return 'Sphere';
      case surfEnum.SurfaceTypeNurbs:
      case surfEnum.SurfaceTypeNurbs.value:
        return 'NURBS';
      // TODO: others unused by now
      default:
        break;
    }
  }
  return null;
};

export const getSurfaceLabelColor = (surfType, surfEnum) => {
  if (surfEnum) {
    switch (surfType) {
      case surfEnum.SurfaceTypeNull:
      case surfEnum.SurfaceTypeNull.value:
        return 'rgb(50%, 50%, 50%)'; // grey
      case surfEnum.SurfaceTypePlane:
      case surfEnum.SurfaceTypePlane.value:
        return 'rgb(100%, 0%, 0%)'; // red
      case surfEnum.SurfaceTypePseudoPlane:
      case surfEnum.SurfaceTypePseudoPlane.value:
        return 'rgb(100%, 35%, 35%)'; // light-red
      case surfEnum.SurfaceTypeCylinder:
      case surfEnum.SurfaceTypeCylinder.value:
        return 'rgb(0%, 90%, 0%)';
      case surfEnum.SurfaceTypeCone:
      case surfEnum.SurfaceTypeCone.value:
        return 'rgb(0%, 0%, 100%)'; // blue
      case surfEnum.SurfaceTypeTorus:
      case surfEnum.SurfaceTypeTorus.value:
        return 'rgb(62.7%, 12.5%, 94.1%)'; // purple
      case surfEnum.SurfaceTypeSphere:
      case surfEnum.SurfaceTypeSphere.value:
        return 'rgb(100%, 75%, 0%)'; // pink
      case surfEnum.SurfaceTypeNurbs:
      case surfEnum.SurfaceTypeNurbs.value:
        return 'rgb(100%, 100%, 0%)'; // yellow
      // TODO: others unused by now
      default:
        break;
    }
  }
  return null;
};

export const getSurfaceExtraLabels = (surfJson, surfEnum, lengthUnit = 1) => {
  if (!surfJson || !surfEnum) return null;

  const { type } = surfJson;
  const extraLabels = [];
  switch (type) {
    case surfEnum.SurfaceTypeCylinder.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: roundTo(2.0 * surfJson.radius * lengthUnit, 1).toFixed(1),
      });
      // extraLabels.push({
      // txt: '𝐡',
      // val: (surfJson.height * lengthUnit).toFixed(1),
      // }); // height (𝐡)
      break;
    case surfEnum.SurfaceTypeCone.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: roundTo(2.0 * surfJson.radius * lengthUnit, 1).toFixed(1),
      });
      // extraLabels.push({
      // txt: '𝐡',
      // val: (surfJson.height * lengthUnit).toFixed(1),
      // }); // height (𝐡)
      if (surfJson.angle > 0) {
        const fact = surfJson.angle < 3.2 ? toDeg : 1;
        extraLabels.push({
          txt: '\u2221', // angle (∡)
          val: `${roundTo(fact * surfJson.angle, 1).toFixed(1)}°`,
          isAngle: true,
        });
      }
      break;
    case surfEnum.SurfaceTypeSphere.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: roundTo(2.0 * surfJson.radius * lengthUnit, 1).toFixed(1),
      });
      break;
    case surfEnum.SurfaceTypeTorus.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: roundTo(2.0 * surfJson.radius * lengthUnit, 1).toFixed(1),
      });
      extraLabels.push({
        txt: '𝐑',
        val: roundTo(surfJson.circle.r * lengthUnit, 1).toFixed(1),
      }); // inner-radius (𝐑)
      break;
    default:
      break;
  }
  return extraLabels;
};

/*
  CURVES
*/

export const getCurveLabel = (crvType, crvEnum) => {
  if (crvEnum) {
    switch (crvType) {
      case crvEnum.CurveTypeNull:
      case crvEnum.CurveTypeNull.value:
        return 'Unknown';
      case crvEnum.CurveTypeLine:
      case crvEnum.CurveTypeLine.value:
        return 'Line';
      case crvEnum.CurveTypeCircle:
      case crvEnum.CurveTypeCircle.value:
        return 'Circle';
      case crvEnum.CurveTypeEllipse:
      case crvEnum.CurveTypeEllipse.value:
        return 'Ellipse';
      case crvEnum.CurveTypeHelix:
      case crvEnum.CurveTypeHelix.value:
        return 'Helix';
      case crvEnum.CurveTypeNurbs:
      case crvEnum.CurveTypeNurbs.value:
        return 'NURBS';
      default:
        // TODO: others unused by now
        break;
    }
  }
  return null;
};

export const getCurveExtraLabels = (crvJson, crvEnum, lengthUnit = 1) => {
  if (!crvJson || !crvEnum) return null;

  const { type } = crvJson;
  const extraLabels = [];
  switch (type) {
    case crvEnum.CurveTypeLine.value:
      {
        const dx = crvJson.end.x - crvJson.start.x;
        const dy = crvJson.end.y - crvJson.start.y;
        const dz = crvJson.end.z - crvJson.start.z;
        const length = Math.sqrt(dx * dx + dy * dy + dz * dz) * lengthUnit;
        if (length > 0) {
          extraLabels.push({
            txt: '', // length
            val: roundTo(length, 2).toFixed(2),
          });
        }
      }
      break;
    case crvEnum.CurveTypeCircle.value:
      extraLabels.push({
        txt: '\u2300', // diameter (⌀)
        val: roundTo(2.0 * crvJson.r * lengthUnit, 1).toFixed(1),
      });
      break;
    case crvEnum.CurveTypeEllipse.value:
      {
        const rMaj = Math.sqrt(
          crvJson.major.x * crvJson.major.x +
            crvJson.major.y * crvJson.major.y +
            crvJson.major.z * crvJson.major.z,
        );
        const rMin = Math.sqrt(
          crvJson.minor.x * crvJson.minor.x +
            crvJson.minor.y * crvJson.minor.y +
            crvJson.minor.z * crvJson.minor.z,
        );
        extraLabels.push({
          txt: 'Ṟ', // major radius
          val: roundTo(rMaj * lengthUnit, 1).toFixed(1),
        });
        extraLabels.push({
          txt: 'ṟ', // minor radius
          val: roundTo(rMin * lengthUnit, 1).toFixed(1),
        });
      }
      break;
    case crvEnum.CurveTypeHelix.value:
      break;
    default:
      break;
  }
  return extraLabels;
};

export const getSurfaceParametersLabel = pars => {
  if (!pars) return null;

  let label = null;
  if (pars.radius) {
    label = `R:${pars.radius.toFixed(2).replace('.00', '')}`;
  }
  if (pars.circle) {
    label += ` r:${pars.circle.r?.toFixed(2).replace('.00', '')}`;
  }
  if (pars.angle) {
    label += ` 𝛼:${((180.0 * pars.angle) / Math.PI)
      ?.toFixed(2)
      .replace('.00', '')}`;
  }
  return label;
};
